import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/dist/query/react';
import { Mutex } from 'async-mutex';

import { env } from '@/config/env';

import { StorageKeysEnum } from '@/enums/storageKeys.enum';

import { clearAuthData } from '@/hooks/useLogout';

import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from './localStorage';

export const prepareHeaders = (headers: Headers) => {
  const jwt = getStorageItem<string>(StorageKeysEnum.jwt);

  if (jwt) {
    headers.set('authorization', `Bearer ${jwt}`);
  }
  if (Boolean(env.TENANT)) {
    headers.set('X-TENANT-ID', String(env.TENANT));
  }
  return headers;
};

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({ baseUrl: env.API_BASE_URL, prepareHeaders });

export const baseQueryWithRedirect: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();

  let result: any = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401 || result?.error?.originalStatus === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      // Make refresh token request if refresh token exists
      const refreshToken = getStorageItem<string>(StorageKeysEnum.refreshToken);
      if (refreshToken) {
        try {
          const refreshResult = await baseQuery(
            {
              url: `${env.API_BASE_URL}/refresh-token`,
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${refreshToken}`,
              },
              body: JSON.stringify({ refresh_token: refreshToken }),
            },
            api,
            extraOptions,
          );

          if (refreshResult?.data) {
            tokenUpdated(refreshResult.data as any);

            // retry the initial query
            result = await baseQuery(args, api, extraOptions);
          } else {
            clearAuthData();
            removeStorageItem(StorageKeysEnum.refreshToken);
          }
        } catch (error) {
          clearAuthData();
          removeStorageItem(StorageKeysEnum.refreshToken);
        } finally {
          release();
        }
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const tokenUpdated = (data: {
  token: string;
  refresh_token: string;
}) => {
  setStorageItem(StorageKeysEnum.jwt, data.token);
  setStorageItem(StorageKeysEnum.refreshToken, data.refresh_token);
};
